import { ChecklistTemplateVersionBump, GetChecklistTemplateQuery, RevisionType } from '../__generated__/graphql';

export type ChecklistTemplate = NonNullable<GetChecklistTemplateQuery['checklistTemplate']>;

export const editableRevisionTypes = [RevisionType.Revision, RevisionType.Simple, RevisionType.Withdrawal];

export const checklistTitle = 'Sjekkliste';

export type EditableCheckItem = {
  key: string;
  name: string;
  allowNotRelevant: boolean;
  requireCommentWhenNotRelevant: boolean;
};
export type EditableTask = EditableCheckItem & {
  weight: string;
};
export type EditablePhase = { key: string; name: string; tasks: EditableTask[] };

export type EditableList = { type: RevisionType; phases: EditablePhase[]; checkItems: EditableCheckItem[] };

let nextKey = 0;
export const newKey = () => `k${nextKey++}`;

export const newTask = (): EditableTask => ({ key: newKey(), name: '', allowNotRelevant: true, requireCommentWhenNotRelevant: false, weight: '1' });

export const newCheckItem = (): EditableCheckItem => ({ key: newKey(), name: '', allowNotRelevant: true, requireCommentWhenNotRelevant: false });

/** No rounding or grouping: the text is read back on publish and must give the same number. */
export const formatWeight = (weight: number) => weight.toLocaleString('nb-NO', { maximumFractionDigits: 20, useGrouping: false });

export const formatWeightTotal = (total: number) => total.toLocaleString('nb-NO', { maximumFractionDigits: 2 });

export function parseWeight(text: string): number | null {
  const trimmed = text.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  return Number(trimmed);
}

export const weightTotal = (phases: EditablePhase[]) =>
  phases.reduce(
    (total, phase) => total + phase.tasks.filter((task) => !!task.name.trim()).reduce((sum, task) => sum + (parseWeight(task.weight) ?? 0), 0),
    0,
  );

export const tasksWithInvalidWeight = (lists: EditableList[]) =>
  lists
    .flatMap((list) => list.phases.flatMap((phase) => phase.tasks))
    .filter((task) => !!task.name.trim() && parseWeight(task.weight) === null)
    .map((task) => task.name.trim());

export const duplicateCheckItems = (lists: EditableList[]) =>
  lists.flatMap((list) => {
    const seen = new Set<string>();
    return list.checkItems
      .map((item) => item.name.trim())
      .filter((name) => !!name)
      .filter((name) => {
        const key = name.toLowerCase();
        if (seen.has(key)) return true;
        seen.add(key);
        return false;
      });
  });

export function toEditableLists(template: ChecklistTemplate | null | undefined): EditableList[] {
  return editableRevisionTypes.map((type) => {
    const list = template?.lists?.find((x) => x?.type === type);

    return {
      type,
      phases: (list?.phases ?? [])
        .filter((x) => !!x)
        .map((phase) => ({
          key: newKey(),
          name: phase.name ?? '',
          tasks: (phase.tasks ?? [])
            .filter((x) => !!x)
            .map((task) => ({
              key: newKey(),
              name: task.name ?? '',
              allowNotRelevant: task.allowNotRelevant,
              requireCommentWhenNotRelevant: task.requireCommentWhenNotRelevant,
              weight: formatWeight(task.weight),
            })),
        })),
      checkItems: (list?.checkItems ?? [])
        .filter((x) => !!x)
        .map((item) => ({
          key: newKey(),
          name: item.name ?? '',
          allowNotRelevant: item.allowNotRelevant,
          requireCommentWhenNotRelevant: item.requireCommentWhenNotRelevant,
        })),
    };
  });
}

export function toPublishInput(lists: EditableList[]) {
  return {
    lists: lists.map((list) => ({
      type: list.type,
      phases: list.phases.map((phase) => ({
        name: phase.name.trim(),
        tasks: phase.tasks
          .filter((task) => !!task.name.trim())
          .map((task) => ({
            ...toPublishableItem(task),
            weight: parseWeight(task.weight) ?? 1,
          })),
      })),
      checkItems: list.checkItems.filter((item) => !!item.name.trim()).map(toPublishableItem),
    })),
  };
}

const toPublishableItem = (item: EditableCheckItem) => ({
  name: item.name.trim(),
  allowNotRelevant: item.allowNotRelevant,
  requireCommentWhenNotRelevant: item.allowNotRelevant && item.requireCommentWhenNotRelevant,
});

export function nextVersion(current: string | null | undefined, bump: ChecklistTemplateVersionBump) {
  const parts = (current ?? '').split('.');
  if (parts.length !== 2) return null;

  const [major, minor] = parts.map(Number);
  if (!Number.isInteger(major) || !Number.isInteger(minor)) return null;

  return bump === ChecklistTemplateVersionBump.Major ? `${major + 1}.0` : `${major}.${minor + 1}`;
}

const withoutBlanks = (lists: EditableList[]) =>
  lists.map((list) => ({
    type: list.type,
    phases: list.phases
      .map((phase) => ({
        name: phase.name.trim(),
        tasks: phase.tasks
          .filter((task) => !!task.name.trim())
          .map((task) => ({
            ...toPublishableItem(task),
            weight: parseWeight(task.weight) ?? task.weight,
          })),
      }))
      .filter((phase) => phase.tasks.length > 0 && !!phase.name),
    checkItems: list.checkItems.filter((item) => !!item.name.trim()).map(toPublishableItem),
  }));

/** Mirrors ChecklistTemplate.RequireOnePhaseOrder on the server. */
export function phaseOrderConflicts(lists: EditableList[]) {
  const cleaned = withoutBlanks(lists);
  const order: string[] = [];
  for (const phase of cleaned.flatMap((list) => list.phases)) {
    if (!order.some((name) => name.toLowerCase() === phase.name.toLowerCase())) order.push(phase.name);
  }

  const position = (name: string) => order.findIndex((x) => x.toLowerCase() === name.toLowerCase());

  return cleaned.flatMap((list) =>
    list.phases.slice(1).flatMap((phase, index) => {
      const before = list.phases[index].name;
      return position(phase.name) < position(before) ? [{ type: list.type, before, after: phase.name }] : [];
    }),
  );
}

export function hasChanges(lists: EditableList[], template: ChecklistTemplate | null | undefined) {
  return JSON.stringify(withoutBlanks(lists)) !== JSON.stringify(withoutBlanks(toEditableLists(template)));
}

export function typesWithoutTasks(lists: EditableList[]) {
  return withoutBlanks(lists)
    .filter((list) => list.phases.length === 0)
    .map((list) => list.type);
}

export function move<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;

  const moved = [...items];
  [moved[index], moved[target]] = [moved[target], moved[index]];
  return moved;
}

export function replaceAt<T>(items: T[], index: number, item: T): T[] {
  return items.map((existing, i) => (i === index ? item : existing));
}

export function removeAt<T>(items: T[], index: number): T[] {
  return items.filter((_, i) => i !== index);
}
